<script setup lang="ts">
import type { TagMapping } from '~/pages/automations/tools/tag-mapping'
import { differenceInSeconds, format, parseISO } from 'date-fns'

import { computed } from 'vue'
import BoltSlashIcon from '~icons/light/bolt-slash'
import ClockIcon from '~icons/light/clock'
import BaseCard from '~/components/BaseCard.vue'

import { DATE_FORMAT, formattedDuration } from '~/utils/date'

const props = defineProps<{
  tool: {
    name: string
    slug: string
    tags: TagMapping[]
    toolType?: TagMapping
    isRecommended?: boolean
    installationCount: number
    linkedFeatureUrl?: string | null
    thumbnailUrl?: string | null
    installedOn?: string
    publishedOn?: string
    ostracizedOn?: string
    averageStats?: {
      improvement?: number | null
    } | null
  }
  customToolName?: string
}>()

const thumbnailUrl = computed(
  () =>
    `url(${
      props.tool.thumbnailUrl
      ?? 'data:image/png;base64,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'
    }`,
)

const ageMetadata = computed(() => {
  const relevantDate = props.tool.ostracizedOn ?? props.tool.publishedOn ?? props.tool.installedOn

  if (!relevantDate)
    return null

  const seconds = differenceInSeconds(new Date(), parseISO(relevantDate))
  const text = `${formattedDuration(seconds, false, 1)} ago`
  const actionVerb = props.tool.ostracizedOn
    ? 'Ostracized'
    : props.tool.publishedOn ? 'Published' : 'Installed'
  const title = `${actionVerb} ${format(parseISO(relevantDate), DATE_FORMAT)}`
  const icon = props.tool.ostracizedOn ? BoltSlashIcon : ClockIcon

  return { title, text, icon }
})
</script>

<template>
  <BaseCard
    class="tw:my-0 tw:flex-auto tw:overflow-hidden tw:text-gray-700 tw:hover:bg-gray-100 tw:dark:text-gray-200 tw:dark:hover:bg-gray-700"
    wrapper-class="tw:h-full tw:flex tw:flex-col"
    data-element="tool-card"
    data-test="tool-card"
  >
    <div
      class="tw:relative tw:h-[173px] tw:w-full tw:bg-[size:auto_70px] tw:bg-no-repeat tw:[&]:bg-center"
      :style="{
        backgroundColor: tool.toolType?.color,
        backgroundImage: thumbnailUrl,
      }"
    >
      <div
        class="tw:absolute tw:bg-black/30 tw:px-3 tw:py-2 tw:text-xs tw:text-white"
      >
        {{ tool.toolType?.label }}: {{ tool.slug }}
      </div>

      <div
        v-if="ageMetadata"
        class="svg-baseline tw:absolute tw:right-0 tw:bg-black/30 tw:px-3 tw:py-2 tw:text-xs tw:text-white"
        :title="ageMetadata.title"
      >
        <component :is="ageMetadata.icon" /> {{ ageMetadata.text }}
      </div>
    </div>
    <div class="tw:flex tw:flex-1 tw:flex-col tw:p-4">
      <div class="tw:mb-2 tw:line-clamp-2 tw:h-[40px] tw:font-semibold">
        {{ customToolName || tool.name }}
      </div>
      <div
        v-if="tool.toolType?.tag === 'pr_check'
          && tool.averageStats?.improvement
          && tool.averageStats.improvement > 0"
        class="tw:mt-auto tw:mb-2 tw:text-xs"
      >
        Customers see <span class="tw:font-semibold tw:text-green-500">
          {{ tool.averageStats?.improvement }}%
        </span> improvement on average!
      </div>
      <div class="tw:mt-auto tw:flex">
        <slot name="footer" />
      </div>
    </div>
  </BaseCard>
</template>
